export const PAGE_SLUG_PATTERN = /^[\da-z][\da-z-]{0,39}$/;
export const PAGES_MAX = 50;
export const PAGE_BLOCKS_MAX = 30;
export const PAGE_TITLE_MAX_LENGTH = 40;
export const PAGE_TEXT_MAX_LENGTH = 1000;
export const PAGE_LIST_MAX_ITEMS = 30;
export const PAGE_IMAGE_RATIOS = ['1:1', '3:4', '9:16', '4:3', '16:9', '2:1', '3:1', '4:1'];
export const PAGE_PRODUCT_SORTS = ['newest', 'oldest', 'lowestPrice', 'highestPrice'];
export const PAGE_BLOCK_TYPES = [
    'image',
    'text',
    'button',
    'spacer',
    'products',
    'liveBroadcasts',
    'scheduledBroadcasts',
    'notices',
];
export const PAGE_DATA_BLOCK_TYPES = [
    'products',
    'liveBroadcasts',
    'scheduledBroadcasts',
    'notices',
];
export const PAGES_DEFAULT_PAYLOAD = {};
const isTree = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const isShortText = (value, max) => typeof value === 'string' && value.length > 0 && value.length <= max && value === value.trim();
const isOptionalText = (value, max) => value === undefined || isShortText(value, max);
const HTTP_URL = /^https?:\/\/\S+$/;
const APP_PATH = /^\/[\w%&./=?[\]-]*$/;
const UUID = /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i;
const isLink = (value) => typeof value === 'string' &&
    value.length <= 1000 &&
    (HTTP_URL.test(value) || (APP_PATH.test(value) && !value.startsWith('//')));
const isCount = (value) => typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= PAGE_LIST_MAX_ITEMS;
const oneOf = (options, value) => typeof value === 'string' && options.includes(value);
const ALLOWED_KEYS = {
    image: ['type', 'imageUrl', 'aspectRatio', 'link'],
    text: ['type', 'title', 'body', 'align'],
    button: ['type', 'label', 'link', 'style'],
    spacer: ['type', 'height'],
    products: ['type', 'title', 'categoryId', 'sort', 'maxItems', 'layout'],
    liveBroadcasts: ['type', 'title', 'maxItems'],
    scheduledBroadcasts: ['type', 'title', 'maxItems'],
    notices: ['type', 'title', 'maxItems'],
};
export function pageBlockErrors(block) {
    if (!isTree(block))
        return ['must be an object'];
    const type = block['type'];
    if (!oneOf(PAGE_BLOCK_TYPES, type))
        return [`unknown block type "${String(type)}"`];
    const errors = [];
    for (const key of Object.keys(block))
        if (!ALLOWED_KEYS[type].includes(key))
            errors.push(`${key}: unknown field of ${type}`);
    const title = () => {
        if (!isOptionalText(block['title'], PAGE_TITLE_MAX_LENGTH))
            errors.push(`title: 1–${PAGE_TITLE_MAX_LENGTH} characters`);
    };
    switch (type) {
        case 'image':
            if (typeof block['imageUrl'] !== 'string' || !HTTP_URL.test(block['imageUrl']))
                errors.push('imageUrl: must be an uploaded http(s) address');
            if (!oneOf(PAGE_IMAGE_RATIOS, block['aspectRatio']))
                errors.push(`aspectRatio: one of ${PAGE_IMAGE_RATIOS.join(', ')}`);
            if (block['link'] !== undefined && !isLink(block['link']))
                errors.push('link: an app path (/…) or an http(s) address');
            break;
        case 'text':
            title();
            if (!isOptionalText(block['body'], PAGE_TEXT_MAX_LENGTH))
                errors.push(`body: 1–${PAGE_TEXT_MAX_LENGTH} characters`);
            if (block['title'] === undefined && block['body'] === undefined)
                errors.push('text: needs a title or a body');
            if (block['align'] !== undefined && !oneOf(['left', 'center'], block['align']))
                errors.push('align: left or center');
            break;
        case 'button':
            if (!isShortText(block['label'], PAGE_TITLE_MAX_LENGTH))
                errors.push(`label: 1–${PAGE_TITLE_MAX_LENGTH} characters`);
            if (!isLink(block['link']))
                errors.push('link: an app path (/…) or an http(s) address');
            if (block['style'] !== undefined && !oneOf(['primary', 'secondary'], block['style']))
                errors.push('style: primary or secondary');
            break;
        case 'spacer': {
            const height = block['height'];
            if (typeof height !== 'number' || !Number.isInteger(height) || height < 0 || height > 200)
                errors.push('height: 0–200');
            break;
        }
        case 'products':
            title();
            if (block['categoryId'] !== undefined) {
                const categoryId = block['categoryId'];
                if (typeof categoryId !== 'string' || !UUID.test(categoryId))
                    errors.push('categoryId: a category id');
            }
            if (!oneOf(PAGE_PRODUCT_SORTS, block['sort']))
                errors.push(`sort: one of ${PAGE_PRODUCT_SORTS.join(', ')}`);
            if (!isCount(block['maxItems']))
                errors.push(`maxItems: 1–${PAGE_LIST_MAX_ITEMS}`);
            if (!oneOf(['grid', 'horizontal'], block['layout']))
                errors.push('layout: grid or horizontal');
            break;
        case 'liveBroadcasts':
        case 'scheduledBroadcasts':
        case 'notices':
            title();
            if (!isCount(block['maxItems']))
                errors.push(`maxItems: 1–${PAGE_LIST_MAX_ITEMS}`);
            break;
    }
    return errors;
}
export function validatePagesPayload(payload) {
    if (!isTree(payload))
        return { ok: false, errors: ['page.index: must be an object'] };
    const errors = [];
    const entries = Object.entries(payload);
    if (entries.length > PAGES_MAX)
        errors.push(`page.index: at most ${PAGES_MAX} pages`);
    for (const [slug, page] of entries) {
        if (!PAGE_SLUG_PATTERN.test(slug)) {
            errors.push(`${slug}: lowercase letters, digits and - (up to 40)`);
            continue;
        }
        if (!isTree(page)) {
            errors.push(`${slug}: must be an object`);
            continue;
        }
        for (const key of Object.keys(page))
            if (!['title', 'visible', 'blocks'].includes(key))
                errors.push(`${slug}.${key}: unknown field`);
        if (!isShortText(page['title'], PAGE_TITLE_MAX_LENGTH))
            errors.push(`${slug}.title: 1–${PAGE_TITLE_MAX_LENGTH} characters`);
        if (typeof page['visible'] !== 'boolean')
            errors.push(`${slug}.visible: true or false`);
        const blocks = page['blocks'];
        if (!Array.isArray(blocks)) {
            errors.push(`${slug}.blocks: must be a list`);
            continue;
        }
        if (blocks.length > PAGE_BLOCKS_MAX)
            errors.push(`${slug}.blocks: at most ${PAGE_BLOCKS_MAX} blocks`);
        for (const [index, block] of blocks.entries())
            for (const error of pageBlockErrors(block))
                errors.push(`${slug}.blocks[${index}].${error}`);
    }
    return errors.length
        ? { ok: false, errors }
        : { ok: true, payload: payload, errors };
}
export function mergePagesPayload(builtIn, received) {
    const result = validatePagesPayload(received);
    return result.ok ? (result.payload ?? builtIn) : null;
}
//# sourceMappingURL=pages.js.map